/*
 * Smooth free shipping progress bar.
 *
 * The cart drawer re-renders its content twice on every cart change, so the theme's own progress animation is
 * restarted from the final value and the bar appears to jump. Here we remember what the bar is showing, and each
 * time a new bar is inserted we animate it from that on-screen value to its new value.
 */
const BAR_ID = 'progress-bar-free-shipping-threshold';
const DURATION = 600;

let current = null; // { from, to, start }

const ease = (t) => 1 - Math.pow(1 - t, 3);

const readProgress = (bar) => {
  const max = Number(bar.getAttribute('aria-valuemax')) || 1;
  return Math.min(1, Number(bar.getAttribute('aria-valuenow')) / max);
};

// Value visible right now, including an animation that is still running
const displayedValue = () => {
  if (!current) {
    return null;
  }

  const t = Math.min(1, (performance.now() - current.start) / DURATION);
  return current.from + (current.to - current.from) * ease(t);
};

const animateBar = (bar) => {
  const to = readProgress(bar);
  const from = displayedValue() ?? to;

  // Run after the theme's own connectedCallback animation, then replace it
  requestAnimationFrame(() => {
    bar.getAnimations().forEach((animation) => animation.cancel());
    bar.animate(
      { '--progress-bar-progress': [from, to] },
      { duration: from === to ? 0 : DURATION, easing: 'cubic-bezier(0.33, 1, 0.68, 1)', fill: 'both' }
    );
  });

  current = { from, to, start: performance.now() };
};

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }

      const bar = node.id === BAR_ID ? node : node.querySelector?.(`#${BAR_ID}`);

      if (bar) {
        animateBar(bar);
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

const initialBar = document.getElementById(BAR_ID);

if (initialBar) {
  current = { from: readProgress(initialBar), to: readProgress(initialBar), start: 0 };
}
